import _ from 'underscore';
import React from 'react';
import PropTypes from 'prop-types';
import { perfMarkerPropTypes } from '@bingads-webui/performance-marker';
import { SelectionMode, Selection } from '@fluentui/react/lib/DetailsList';
import { CampaignShimmeredDetailsList } from '@bingads-webui-grid/campaign-details-list';
import { ProgressiveLoadingBlock } from '@bingads-webui-campaign-react/shell-ui-common';
import { withReportDataSource } from '@bingads-webui-campaign/reports-v4-data-source';
import { withI18nConsumer } from '@bingads-webui-react/i18n-context';
import { withRouter } from '@bingads-webui-campaign-react/router';
import { ToolbarContainer } from '@bingads-webui-campaign-react/toolbar-container';
import { getToolbarEditor } from '@bingads-webui-campaign-react/toolbar-plugin';
import { FilterViewManager } from '@bingads-webui-campaign/reports-v4-filter-view-manager';
import { SchemaI18n } from '@bingads-webui-universal/schema-i18n';
import { legacyKeys, util } from '@bingads-webui-campaign/report-shared';
import { buildLegacyI18n } from '@bingads-webui-campaign-react/smart-i18n';
import { UiBlocker } from '@bingads-webui-react/ui-blocker';
import { convertDateRangePresetToRangeId, convertComparisonDateRangeOptionToComparedKey } from '@bingads-webui-campaign/date-range-utilities';
import { WaterfallFilterType } from '@bingads-webui-campaign/filter-storage';
import { withScope, QueryParam, $current } from '@bingads-webui-react/scope-service';
import { withGlobalFilter } from '@bingads-webui-campaign-react/global-filter-context';
import { withServerContext } from '@bingads-webui-campaign-react/server-context';
import { LearnMoreLink } from '@bingads-webui-campaign-react/help';
import { ReportsV4Footer } from '@bingads-webui-campaign/reports-v4-footer';
import { withCtrLogger } from '@bingads-webui-campaign/reports-v4-telemetry';
import { withIframeDownload } from '@bingads-webui/download-service';
import { withDFMCache } from '@bingads-webui/dfm-cache';
import { withSiteMap } from '@bingads-webui-campaign-react/with-site-map';

import { getFilterConfig } from './toolbar/filter-config';
import { getMyReportsGridToolbarConfig } from './toolbar/toolbar-config';
import { buildColumns } from './columns';
import bulkEditActionsFactory from './actions/bulk-edit';
import { isDeprecatedDimensionReport, transformTaskItem } from './helper';
/* global Map */

const { convertDateTimeStringToCivilDate, dummyDate } = util;

class MyReportsGrid extends React.PureComponent {
  constructor(props) {
    super(props);

    const { toolbarUnselectedConfig, toolbarSelectedConfig } = this.getToolBarConfig([]);

    this.gridSelection = new Selection({
      onSelectionChanged: () => {
        this.onSelectedItemUpdated(this.gridSelection.getSelection());
      },
    });

    const {
      i18n, serverContext, ctrLogger, type,
    } = this.props;

    this.legacyI18n = buildLegacyI18n(i18n, legacyKeys);
    this.initBulkActions();
    const {
      inlineDownloadDuplicate,
    } = this;
    this.state = {
      columns: buildColumns({
        i18n,
        legacyI18n: this.legacyI18n,
        serverContext,
        ctrLogger,
        inlineDownloadDuplicate,
        handleInlineEditSuccess: () => this.fetchData(),
        type,
        permissions: this.props.serverContext.DynamicPermissions,
      }),
      items: null,
      forceHideToolbar: false,
      toolbarUnselectedConfig: new Map(toolbarUnselectedConfig),
      toolbarSelectedConfig: new Map(toolbarSelectedConfig),
      selectedItems: [],
      sort: '',
      filter: {},
      fetching: false,
      pageNumber: 1,
      pageSize: 20,
      total: 1,
      sortedColumn: {},
      blocking: false,
    };
  }

  componentDidMount() {
    this.fetchData(1, true);
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.columns !== this.state.columns) {
      this.fetchData();
    }
  }

  onClearSelection = () => {
    this.gridSelection.setAllSelected(false);
  };

  onPageChange = (page) => {
    this.fetchData(page);
  };

  onPageSizeChange = (pageSize) => {
    this.setState({ pageSize }, () => {
      this.fetchData();
    });
  };

  onSelectedItemUpdated(selectedItems) {
    const { toolbarSelectedConfig } = this.getToolBarConfig(selectedItems);
    this.setState({
      selectedItems,
      toolbarSelectedConfig,
    });
  }

  getToolBarConfig = selectedItems =>
    getMyReportsGridToolbarConfig({
      i18n: this.props.i18n,
      type: this.props.type,
      selectedItems,
      permissions: this.props.serverContext.DynamicPermissions,
      scope: this.props.scope,
      siteMap: this.props.siteMap,
      reportDuplicate: this.reportDuplicate,
      reportDownload: this.reportDownload,
      reportDelete: this.reportDelete,
    });

  initBulkActions() {
    const {
      i18n,
      scope,
      serverContext: {
        CurrentUser: {
          CultureDateTimeFormat: {
            ShortDatePattern,
          },
          LocaleID,
        },
      },
      serverContext,
      iframeDownload,
      updateWaterfallFilter,
    } = this.props;

    const { legacyI18n } = this;

    function getUrlSearch(params = {}) {
      const mergedParams = _.omit({
        uid: $current,
        cid: $current,
        aid: scope.values.aid,
        ...params,
      }, value => !value);
      const mergedScope = scope.getMerged(mergedParams);

      if (params.entity === 'Hotel') {
        delete mergedScope.campaignId;
        delete mergedScope.adGroupId;
      }

      if (isDeprecatedDimensionReport(params.entity)) {
        mergedScope.isDrprecated = true;
      }

      return `?${scope.stringify(mergedScope)}`;
    }

    function convertSelectionDateRangeToDateFilter({
      DateRange,
      ComparisonDateRange,
    }) {
      const startDate = convertDateTimeStringToCivilDate(DateRange.StartDate);
      const endDate = convertDateTimeStringToCivilDate(DateRange.EndDate);
      const basic = {
        RangeStartDate: i18n.formatCivilDate(startDate || dummyDate, { raw: ShortDatePattern }),
        RangeEndDate: i18n.formatCivilDate(endDate || dummyDate, { raw: ShortDatePattern }),
        RangeId: convertDateRangePresetToRangeId(DateRange.DateRangePreset),
        Rangelcid: LocaleID,
      };

      let compared = null;
      if (ComparisonDateRange) {
        const comparedStartDate = convertDateTimeStringToCivilDate(ComparisonDateRange.StartDate);
        const comparedEndDate = convertDateTimeStringToCivilDate(ComparisonDateRange.EndDate);
        compared = {
          formattedStart: i18n.formatCivilDate(comparedStartDate || dummyDate, { raw: ShortDatePattern }),
          formattedEnd: i18n.formatCivilDate(comparedEndDate || dummyDate, { raw: ShortDatePattern }),
          key: convertComparisonDateRangeOptionToComparedKey(ComparisonDateRange.ComparisonDateRangeOption),
        };
      }

      return {
        ...basic,
        compared,
      };
    }

    function restoreDateFilter(dateFilter) {
      updateWaterfallFilter({
        filterType: WaterfallFilterType.DateFilter,
        filter: dateFilter,
      });
    }

    this.bulkEditActions = bulkEditActionsFactory({
      i18n,
      onActionSuccess: () => this.fetchData(),
      resetSelection: () => this.gridSelection.setAllSelected(false),
      legacyI18n,
      scopeHistoryPush: scope.history.push,
      getUrlSearch,
      convertSelectionDateRangeToDateFilter,
      restoreDateFilter,
      serverContext,
      iframeDownload,
      history: this.props.history,
      rerunReport: this.props.rerunReportAndWaitForComplete,
      deleteReports: this.props.deleteReports,
    });
  }

  inlineDownloadDuplicate = (reportItem) => {
    this.bulkEditActions.duplicate([reportItem]);
  };

  logToolbarAction(actionName) {
    const { ctrLogger } = this.props;
    ctrLogger.click({
      target: actionName,
      extraData: this.state.selectedItems.map(item => _.pick(item, 'Id', 'Type', 'ReportType', 'Entity')),
    });
  }

  reportDuplicate = () => {
    this.logToolbarAction('Duplicate');
    this.bulkEditActions.duplicate(this.state.selectedItems);
  };

  reportDownload = () => {
    this.logToolbarAction('Download');
    this.bulkEditActions.download(this.state.selectedItems);
  };

  reportDelete = () => {
    this.logToolbarAction('Delete');
    this.setState({ blocking: true });
    this.bulkEditActions.del(this.state.selectedItems).finally(() => {
      this.setState({ blocking: false });
    });
  };

  resetToolbarVisibility = ({ forceHideToolbar }) => {
    this.setState({ forceHideToolbar });
  };

  sortAndFetch(column) {
    this.setState({
      sort: `${column.fieldName} ${column.isSortedDescending ? 'desc' : 'asc'}`,
      sortedColumn: column,
    }, () => {
      this.fetchData();
    });
  }

  fetchData(page = 1, enableDFM = false) {
    if (this.state.fetching) {
      return;
    }
    const { type, perfMarker } = this.props;
    if (this.state.items) {
      this.actionPerfMarker = perfMarker.createChild('My_Reports_Action');
    }
    this.setState({
      items: null,
      fetching: true,
    });
    const {
      filter, sort, pageSize,
    } = this.state;

    perfMarker.willFetchData();
    if (this.actionPerfMarker) {
      this.actionPerfMarker.willFetchData();
    }

    const getMyReports = _.partial(this.props.getMyReports, {
      type,
      sort,
      filter: Object.keys(filter).length > 0 ? (filter.$and || [filter]) : [],
      top: pageSize,
      skip: (page - 1) * pageSize,
    });
    const getCustomizedReportsDFM = enableDFM ?
      withDFMCache(getMyReports, `get${type}ReportsDFM`, this) : getMyReports;

    getCustomizedReportsDFM()
      .then((json) => {
        const originItems = json.value;
        const finalItems = originItems.map(transformTaskItem);
        perfMarker.willRender();
        if (this.actionPerfMarker) {
          this.actionPerfMarker.willRender();
        }
        this.setState({
          items: finalItems,
          fetching: false,
          pageNumber: page,
        });
        this.setState({
          total: json['@odata.count'],
        });
        this.props.updateCount(json['@odata.count']);
      }).catch(() => {
        this.setState({
          items: [],
          fetching: false,
        });
      });
  }

  renderFilterViewManager() {
    const { i18n } = this.props;
    const { toolbarSelectedConfig, toolbarUnselectedConfig } = this.state;

    const toolbarEditor = getToolbarEditor({
      toolbarConfig: {
        toolbarSelectedConfig,
        toolbarUnselectedConfig,
      },
    });

    const filterConfig = getFilterConfig({ i18n });

    return (
      <div>
        <FilterViewManager
          updateToolbar={toolbarEditor.update}
          resetToolbarVisibility={this.resetToolbarVisibility}
          legacyI18n={i18n}
          hideSaveFilterButton
          {...filterConfig}
          schemaI18n={new SchemaI18n({ i18n })}
          editableFilter={this.state.filter}
          setEditableFilter={(query, source) => {
            this.setState({ filter: query }, () => {
              this.fetchData();
            });
            const { ctrLogger } = this.props;
            ctrLogger.click({
              target: source,
              extraData: query,
            });
          }}
        />
      </div>
    );
  }

  renderToolbarContainer() {
    const { i18n } = this.props;

    const selectionCount = this.state.selectedItems ? this.state.selectedItems.length : 0;
    const showToolbarSelected = selectionCount > 0;
    const showToolbarUnselected = !showToolbarSelected;
    return (
      <ToolbarContainer
        i18n={i18n}
        forceHideToolbar={this.state.forceHideToolbar}
        showToolbarSelected={showToolbarSelected}
        showToolbarUnselected={showToolbarUnselected}
        toolbarSelectedConfig={this.state.toolbarSelectedConfig}
        toolbarUnselectedConfig={this.state.toolbarUnselectedConfig}
        onClear={this.onClearSelection}
        selectionCount={selectionCount}
      />
    );
  }

  render() {
    const {
      classes, i18n, type, perfMarker,
    } = this.props;

    const {
      pageNumber, pageSize, total, filter, sortedColumn, blocking, items, columns,
    } = this.state;

    const { onPageChange, onPageSizeChange } = this;
    const placeholderWithFilter = i18n.getString(_TL_('There are no reports based on your filtered search.'));
    const placeholderWithoutFilter = (
      <React.Fragment>
        <span>{i18n.getString(_TL_('There are no saved custom reports. '))}</span>
        <LearnMoreLink
          isUnderlineLink={false}
          linkType="helpPopup"
          topic="app54700"
          linkText={i18n.getString(_TL_('Learn about saving custom reports.'))}
        />
      </React.Fragment>
    );
    const toolbar = (
      <React.Fragment>
        {this.renderToolbarContainer()}
        {this.renderFilterViewManager()}
      </React.Fragment>
    );
    return (
      <React.Suspense fallback={<ProgressiveLoadingBlock />}>
        <div className={classes.myReportsGrid}>
          <UiBlocker
            blocking={blocking}
            loadingText={i18n.getString(_TL_('Loading...'))}
            classes={{
              blockUiContainer: 'blocker',
              blockerIndicator: 'blocker-indicator',
            }}
          >
            <CampaignShimmeredDetailsList
              stickyHeader
              toolbar={toolbar}
              enableShimmer={items === null}
              items={items || []}
              columns={columns}
              selectionMode={type === 'Saved' ? SelectionMode.multiple : SelectionMode.single}
              selection={this.gridSelection}
              onSort={column => this.sortAndFetch(column)}
              sortedColumn={sortedColumn}
              placeholder={filter.length > 0 ? placeholderWithFilter : placeholderWithoutFilter}
              freezingColumnCount={1}
              didUpdate={(prev) => {
                if (prev.enableShimmer && items) {
                  perfMarker.done();
                  if (this.actionPerfMarker) {
                    this.actionPerfMarker.done();
                  }
                }
              }}
              pager={{
                pageNumber,
                pageSize,
                total,
                onPageChange,
                onPageSizeChange,
              }}
            />
          </UiBlocker>
          <ReportsV4Footer />
        </div>
      </React.Suspense>
    );
  }
}

MyReportsGrid.propTypes = {
  classes: PropTypes.shape({}).isRequired,
  i18n: PropTypes.shape({}).isRequired,
  getMyReports: PropTypes.func.isRequired,
  rerunReportAndWaitForComplete: PropTypes.func.isRequired,
  deleteReports: PropTypes.func.isRequired,
  type: PropTypes.string.isRequired,
  updateCount: PropTypes.func.isRequired,
  scope: PropTypes.instanceOf(QueryParam).isRequired,
  updateWaterfallFilter: PropTypes.func.isRequired,
  serverContext: PropTypes.shape({}).isRequired,
  iframeDownload: PropTypes.func.isRequired,
  history: PropTypes.shape({}).isRequired,
  ctrLogger: PropTypes.shape({}).isRequired,
  perfMarker: perfMarkerPropTypes.isRequired,
};

MyReportsGrid.defaultProps = {
};

const wrappers = [
  withI18nConsumer,
  withReportDataSource,
  withScope,
  withServerContext,
  withSiteMap,
  withGlobalFilter,
  withRouter,
  withIframeDownload,
  _.partial(withCtrLogger, _, 'ReportsV4MyReports'),
];

const Wrapped = _.compose(...wrappers)(MyReportsGrid);

export { Wrapped as MyReportsGrid };
